/**
 * Pure helpers to compute new canvas arrays when nudging selected elements by (dx, dy).
 * Mirrors main vs off-canvas resolution used in deleteAllSelectedObjects (MyCanvas.js).
 */
import { constrainNudgePosition } from "./nudgeConstraints";

function applyConstrainedNudge(el, elementType, isMainCanvas, dx, dy, constraintOptions) {
  const nx = (el.x ?? 0) + dx;
  const ny = (el.y ?? 0) + dy;
  const c = constrainNudgePosition(
    el,
    elementType,
    nx,
    ny,
    isMainCanvas,
    constraintOptions,
  );
  return { ...el, x: c.x, y: c.y };
}

function isElementInMainCanvas(elementId, elementType, ctx) {
  const {
    rectangles,
    circles,
    arrows,
    lines,
    doubleSidedArrows,
    images,
    texts,
  } = ctx;

  if (!elementId || !elementType) return false;
  switch (elementType) {
    case "Rect":
      return rectangles.some((r) => r.id === elementId);
    case "Circle":
      return circles.some((c) => c.id === elementId);
    case "Image":
      return images.some((i) => i.id === elementId);
    case "Text":
      return texts.some((t) => t.id === elementId);
    case "Arrow":
      return (
        arrows.some((a) => a.id === elementId) ||
        lines.some((l) => {
          if (l.id !== elementId) return false;
          const hasStartArrow =
            l.hasStartArrow === true ||
            l.pointerAtBeginning === true ||
            l.pointerAtBeginning === "true";
          const hasEndArrow =
            l.hasEndArrow === true ||
            l.pointerAtEnd === true ||
            l.pointerAtEnding === true ||
            l.pointerAtEnd === "true" ||
            l.pointerAtEnding === "true";
          return (hasStartArrow || hasEndArrow) && !(hasStartArrow && hasEndArrow);
        })
      );
    case "Line":
      return lines.some((l) => l.id === elementId);
    case "DoubleSidedArrow":
      return (
        doubleSidedArrows.some((d) => d.id === elementId) ||
        lines.some((l) => {
          if (l.id !== elementId) return false;
          const hasStartArrow =
            l.hasStartArrow === true ||
            l.pointerAtBeginning === true ||
            l.pointerAtBeginning === "true";
          const hasEndArrow =
            l.hasEndArrow === true ||
            l.pointerAtEnd === true ||
            l.pointerAtEnding === true ||
            l.pointerAtEnd === "true" ||
            l.pointerAtEnding === "true";
          return hasStartArrow && hasEndArrow;
        })
      );
    default:
      return false;
  }
}

function findElementForSelection(id, type, next) {
  switch (type) {
    case "Rect":
      return (
        next.rectangles.find((r) => r.id === id) ||
        next.offCanvasRectangles.find((r) => r.id === id)
      );
    case "Circle":
      return (
        next.circles.find((c) => c.id === id) ||
        next.offCanvasCircles.find((c) => c.id === id)
      );
    case "Image":
      return next.images.find((i) => i.id === id) || next.offCanvasImages.find((i) => i.id === id);
    case "Text":
      return next.texts.find((t) => t.id === id) || next.offCanvasTexts.find((t) => t.id === id);
    case "Line": {
      const l = next.lines.find((x) => x.id === id) || next.offCanvasLines.find((x) => x.id === id);
      return l;
    }
    case "Arrow": {
      const inLine =
        next.lines.find((l) => l.id === id) || next.offCanvasLines.find((l) => l.id === id);
      if (inLine) return inLine;
      return next.arrows.find((a) => a.id === id) || next.offCanvasArrows.find((a) => a.id === id);
    }
    case "DoubleSidedArrow": {
      const inLine =
        next.lines.find((l) => l.id === id) || next.offCanvasLines.find((l) => l.id === id);
      if (inLine) return inLine;
      return (
        next.doubleSidedArrows.find((d) => d.id === id) ||
        next.offCanvasDoubleSidedArrows.find((d) => d.id === id)
      );
    }
    default:
      return null;
  }
}

/**
 * @param {Array<{ id: string, type: string }>} allObjectsToNudge
 * @param {number} dx
 * @param {number} dy
 * @param {Object} state — current canvas arrays (main + off-canvas)
 * @param {Object} [constraintOptions] — stage sizes for constrainNudgePosition (see nudgeConstraints.js)
 * @returns {{ rectangles, offCanvasRectangles, circles, offCanvasCircles, arrows, offCanvasArrows, doubleSidedArrows, offCanvasDoubleSidedArrows, lines, offCanvasLines, texts, offCanvasTexts, images, offCanvasImages, selectionSnapshots, didChange }}
 */
export function computeNudgedCanvasState(allObjectsToNudge, dx, dy, state, constraintOptions = {}) {
  const {
    rectangles,
    offCanvasRectangles,
    circles,
    offCanvasCircles,
    arrows,
    offCanvasArrows,
    doubleSidedArrows,
    offCanvasDoubleSidedArrows,
    lines,
    offCanvasLines,
    texts,
    offCanvasTexts,
    images,
    offCanvasImages,
  } = state;

  let nextRectangles = rectangles;
  let nextOffCanvasRectangles = offCanvasRectangles;
  let nextCircles = circles;
  let nextOffCanvasCircles = offCanvasCircles;
  let nextArrows = arrows;
  let nextOffCanvasArrows = offCanvasArrows;
  let nextDoubleSidedArrows = doubleSidedArrows;
  let nextOffCanvasDoubleSidedArrows = offCanvasDoubleSidedArrows;
  let nextLines = lines;
  let nextOffCanvasLines = offCanvasLines;
  let nextTexts = texts;
  let nextOffCanvasTexts = offCanvasTexts;
  let nextImages = images;
  let nextOffCanvasImages = offCanvasImages;

  const ctx = {
    rectangles,
    circles,
    arrows,
    lines,
    doubleSidedArrows,
    images,
    texts,
  };

  for (const obj of allObjectsToNudge) {
    if (!obj || !obj.id || !obj.type) continue;

    const isMainCanvas = isElementInMainCanvas(obj.id, obj.type, ctx);

    switch (obj.type) {
      case "Rect":
        if (isMainCanvas) {
          nextRectangles = nextRectangles.map((r) =>
            r.id === obj.id
              ? applyConstrainedNudge(r, obj.type, isMainCanvas, dx, dy, constraintOptions)
              : r
          );
        } else {
          nextOffCanvasRectangles = nextOffCanvasRectangles.map((r) =>
            r.id === obj.id
              ? applyConstrainedNudge(r, obj.type, isMainCanvas, dx, dy, constraintOptions)
              : r
          );
        }
        break;
      case "Circle":
        if (isMainCanvas) {
          nextCircles = nextCircles.map((c) =>
            c.id === obj.id
              ? applyConstrainedNudge(c, obj.type, isMainCanvas, dx, dy, constraintOptions)
              : c
          );
        } else {
          nextOffCanvasCircles = nextOffCanvasCircles.map((c) =>
            c.id === obj.id
              ? applyConstrainedNudge(c, obj.type, isMainCanvas, dx, dy, constraintOptions)
              : c
          );
        }
        break;
      case "Image":
        if (isMainCanvas) {
          nextImages = nextImages.map((i) =>
            i.id === obj.id
              ? applyConstrainedNudge(i, obj.type, isMainCanvas, dx, dy, constraintOptions)
              : i
          );
        } else {
          nextOffCanvasImages = nextOffCanvasImages.map((i) =>
            i.id === obj.id
              ? applyConstrainedNudge(i, obj.type, isMainCanvas, dx, dy, constraintOptions)
              : i
          );
        }
        break;
      case "Text":
        if (isMainCanvas) {
          nextTexts = nextTexts.map((t) =>
            t.id === obj.id
              ? applyConstrainedNudge(t, obj.type, isMainCanvas, dx, dy, constraintOptions)
              : t
          );
        } else {
          nextOffCanvasTexts = nextOffCanvasTexts.map((t) =>
            t.id === obj.id
              ? applyConstrainedNudge(t, obj.type, isMainCanvas, dx, dy, constraintOptions)
              : t
          );
        }
        break;
      case "Arrow": {
        const arrowInLines = isMainCanvas
          ? lines.find((l) => l.id === obj.id)
          : offCanvasLines.find((l) => l.id === obj.id);

        if (arrowInLines) {
          if (isMainCanvas) {
            nextLines = nextLines.map((l) =>
              l.id === obj.id
                ? applyConstrainedNudge(l, obj.type, isMainCanvas, dx, dy, constraintOptions)
                : l
            );
          } else {
            nextOffCanvasLines = nextOffCanvasLines.map((l) =>
              l.id === obj.id
                ? applyConstrainedNudge(l, obj.type, isMainCanvas, dx, dy, constraintOptions)
                : l
            );
          }
        } else if (isMainCanvas) {
          nextArrows = nextArrows.map((a) =>
            a.id === obj.id
              ? applyConstrainedNudge(a, obj.type, isMainCanvas, dx, dy, constraintOptions)
              : a
          );
        } else {
          nextOffCanvasArrows = nextOffCanvasArrows.map((a) =>
            a.id === obj.id
              ? applyConstrainedNudge(a, obj.type, isMainCanvas, dx, dy, constraintOptions)
              : a
          );
        }
        break;
      }
      case "Line":
        if (isMainCanvas) {
          nextLines = nextLines.map((l) =>
            l.id === obj.id
              ? applyConstrainedNudge(l, obj.type, isMainCanvas, dx, dy, constraintOptions)
              : l
          );
        } else {
          nextOffCanvasLines = nextOffCanvasLines.map((l) =>
            l.id === obj.id
              ? applyConstrainedNudge(l, obj.type, isMainCanvas, dx, dy, constraintOptions)
              : l
          );
        }
        break;
      case "DoubleSidedArrow": {
        const doubleArrowInLines = isMainCanvas
          ? lines.find((l) => l.id === obj.id)
          : offCanvasLines.find((l) => l.id === obj.id);

        if (doubleArrowInLines) {
          if (isMainCanvas) {
            nextLines = nextLines.map((l) =>
              l.id === obj.id
                ? applyConstrainedNudge(l, obj.type, isMainCanvas, dx, dy, constraintOptions)
                : l
            );
          } else {
            nextOffCanvasLines = nextOffCanvasLines.map((l) =>
              l.id === obj.id
                ? applyConstrainedNudge(l, obj.type, isMainCanvas, dx, dy, constraintOptions)
                : l
            );
          }
        } else if (isMainCanvas) {
          nextDoubleSidedArrows = nextDoubleSidedArrows.map((d) =>
            d.id === obj.id
              ? applyConstrainedNudge(d, obj.type, isMainCanvas, dx, dy, constraintOptions)
              : d
          );
        } else {
          nextOffCanvasDoubleSidedArrows = nextOffCanvasDoubleSidedArrows.map((d) =>
            d.id === obj.id
              ? applyConstrainedNudge(d, obj.type, isMainCanvas, dx, dy, constraintOptions)
              : d
          );
        }
        break;
      }
      default:
        break;
    }
  }

  const next = {
    rectangles: nextRectangles,
    offCanvasRectangles: nextOffCanvasRectangles,
    circles: nextCircles,
    offCanvasCircles: nextOffCanvasCircles,
    arrows: nextArrows,
    offCanvasArrows: nextOffCanvasArrows,
    doubleSidedArrows: nextDoubleSidedArrows,
    offCanvasDoubleSidedArrows: nextOffCanvasDoubleSidedArrows,
    lines: nextLines,
    offCanvasLines: nextOffCanvasLines,
    texts: nextTexts,
    offCanvasTexts: nextOffCanvasTexts,
    images: nextImages,
    offCanvasImages: nextOffCanvasImages,
  };

  let didChange = false;
  for (const obj of allObjectsToNudge) {
    if (!obj?.id || !obj?.type) continue;
    const before = findElementForSelection(obj.id, obj.type, state);
    const after = findElementForSelection(obj.id, obj.type, next);
    if (!before || !after) continue;
    if (before.x !== after.x || before.y !== after.y) {
      didChange = true;
      break;
    }
  }

  const selectionSnapshots = allObjectsToNudge.map((obj) => {
    if (!obj || !obj.id || !obj.type) return obj;
    const el = findElementForSelection(obj.id, obj.type, next);
    return el ? { ...el, type: obj.type } : obj;
  });

  return { ...next, selectionSnapshots, didChange };
}
